import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import AboutContent from "@/components/AboutContent";
import { useLang } from "@/i18n/LanguageContext";

export default function ContactPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(en ? "Message sent. We will reply shortly." : "Message envoyé. Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-32 pb-20" data-testid="contact-page">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <div className="meta-label text-cinabre">§ Contact</div>
          <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.9] tracking-tight mt-6">
            {en ? <>Write to the <em className="italic">Thread</em>.</> : <>Écrire au <em className="italic">Fil</em>.</>}
          </h1>
          <p className="mt-6 text-fg/75 leading-relaxed max-w-md">
            {en ? "Artists, patrons, institutions, towns — write to us, we take the time to reply." : "Artistes, mécènes, institutions, communes — écrivez-nous, nous prenons le temps de répondre."}
          </p>
          <div className="mt-10 space-y-2 font-mono text-[12px] tracking-[0.16em] uppercase text-fg/70">
            <div>arteurs.lecollectif@gmail.com</div>
            <div>40 Grande Rue, 31620 Castelnau d'Estrétefonds</div>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7" data-testid="contact-form">
          {[
            { k: "name", label: en ? "Your name" : "Votre nom", type: "text", required: true },
            { k: "email", label: en ? "Your email" : "Votre email", type: "email", required: true },
            { k: "subject", label: en ? "Subject" : "Sujet", type: "text" },
          ].map((f) => (
            <div key={f.k} className="mb-8">
              <label className="meta-label text-fg/60 block mb-2">{f.label}{f.required && " *"}</label>
              <input
                required={f.required}
                type={f.type}
                value={form[f.k]}
                onChange={onChange(f.k)}
                className="w-full bg-transparent border-b border-fg/30 focus:border-cinabre outline-none py-3 font-serif text-2xl"
                data-testid={`contact-${f.k}`}
              />
            </div>
          ))}
          <div className="mb-8">
            <label className="meta-label text-fg/60 block mb-2">Message *</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={onChange("message")}
              className="w-full bg-transparent border-b border-fg/30 focus:border-cinabre outline-none py-3 font-serif text-xl resize-none"
              data-testid="contact-message"
            />
          </div>
          <button disabled={loading} className="btn-editorial" data-testid="contact-submit">
            {loading ? (en ? "Sending..." : "Envoi...") : (en ? "Send" : "Envoyer")} <ArrowRight size={14} />
          </button>
        </form>
      </div>

      {/* Qui sommes-nous — embarqué directement sous le formulaire pour lecture immédiate */}
      <div className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1400px] mx-auto border-t border-fg/15 pt-16 md:pt-24" data-testid="contact-about">
        <div className="meta-label text-cinabre">§ {en ? "Who we are" : "Qui sommes-nous ?"}</div>
        <h2 className="mt-6 font-serif font-light text-5xl md:text-7xl leading-[0.92] tracking-tight">
          Les <em className="italic">Arteurs</em>.
        </h2>
        <p className="mt-6 font-serif italic text-xl md:text-2xl text-fg/85 leading-[1.4] max-w-3xl">
          {en ? "« An artistic collective committed to creation, research and transmission. »" : "« Un collectif artistique engagé dans la création, la recherche et la transmission. »"}
        </p>
        <p className="mt-5 max-w-3xl text-fg/80 leading-relaxed">
          {en ? <>We develop projects that connect territories, contemporary narratives and forms of presence, with one ambition: <strong>making art a space of individual and collective transformation.</strong></> : <>Nous développons des projets qui relient les territoires, les récits contemporains et les formes de présence, avec une ambition : <strong>faire de l'art un espace de transformation individuelle et collective.</strong></>}
        </p>

        <AboutContent compact />
      </div>
    </main>
  );
}
